import { useState, useEffect, useContext, useRef } from "react";
import "./PlanoTipo.css";
import { AppContext } from "../App";
import Loader from "./ui/Loader";
import Selector from "./ui/Selector";
import PdfViewer from "./ui/PdfViewer";
import { fetch_projectList, fetch_inventory, fetch_pdf } from "../utils/data";
import WarningFormat from "./ui/WarningFormat";
import TogglesContenido from "./ui/TogglesContenido";
// import sample from "sample.pdf";

export default function PlanoTipo() {
  //#region VARS --------------------------------------------------------
  const { clientId, showLogs } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(undefined);
  const [projectList, setProjectList] = useState();
  const [selectedProject, setSelectedProject] = useState();
  const [spreadsheetList, setSpreadsheetList] = useState();
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState();
  const [unitsList, setUnitsList] = useState();
  const [selectedUnit, setSelectedUnit] = useState();
  const [pdfDoc, setPdfDoc] = useState();
  const [errorData, setErrorData] = useState();

  const dataBaseUrl = useRef();
  const unitsBaseUrl = useRef();

  const abortRef = useRef();
  const contenidoRef = useRef();
  const hasAreasExactas = useRef(false);
  //#endregion VARS -----------------------------------------------------

  //#region FETCH PROJECT LIST --------------------------------------------------------
  useEffect(() => {
    if (projectList) return;

    const fetchProjetList = async () => {
      setIsLoading("Cargando proyectos");
      const response = await fetch_projectList(clientId);
      if (showLogs.current) console.log("fetchProjetList", response);

      if (response?.error || response?.message) {
        const messages = processErrorMessages(response);
        showErrorFeedback({ title: response.message, messages });
        return;
      }

      //URL Proyectos
      const urlProyectos = response?.urlProyectos?.split(
        "https://docs.google.com/spreadsheets/d/"
      )[1];

      dataBaseUrl.current = urlProyectos ? response?.urlProyectos : "";

      //Projects List Options
      const projectsList = response?.googleSheetsList || response?.bim360List;

      const projectOptions = response?.googleSheetsList
        ? projectsList
            ?.filter((row) => row.idProyecto && row.nombreProyecto)
            .map((row) => {
              return { value: row.idProyecto, label: row.nombreProyecto };
            })
        : projectsList.map((row) => {
            return { value: row, label: row };
          });

      //Set project list
      if (showLogs.current) console.log("projectOptions", projectOptions);
      setProjectList(projectOptions);
      setIsLoading(undefined);
    };

    if (!isLoading) fetchProjetList();
  }, []);
  //#endregion FETCH PROJECT LIST -----------------------------------------------------

  //#region SELECTED PROJECT --------------------------------------------------------
  useEffect(() => {
    setErrorData(undefined);
    setSpreadsheetList(undefined);
    setSelectedSpreadsheet(undefined);
    setSelectedUnit(undefined);
    setUnitsList(undefined);
    setPdfDoc(undefined);
    contenidoRef.current = undefined;
    hasAreasExactas.current = false;
  }, [selectedProject]);
  //#endregion SELECTED PROJECT -----------------------------------------------------

  //#region SELECTED SPREADSHEET --------------------------------------------------------
  useEffect(() => {
    setErrorData(undefined);
    setSelectedUnit(undefined);
    setUnitsList(undefined);
    setPdfDoc(undefined);
  }, [selectedSpreadsheet]);
  //#endregion SELECTED SPREADSHEET -----------------------------------------------------

  //#region PROCESS INVENTORY --------------------------------------------------------
  useEffect(() => {
    if (!selectedProject?.value) {
      return; // Do nothing if no project is selected
    }

    if (unitsList) {
      return; // Don't re-fetch if we already have units for the current setup.
    }

    const performFetch = async () => {
      // Error check for project value
      if (!selectedProject?.value) {
        showErrorFeedback({
          title: `El proyecto seleccionado "${selectedProject?.label}" no tiene definida la columna "idProyecto" en la base de datos: ${dataBaseUrl.current}`,
          messages: [],
        });
        return;
      }

      setIsLoading(
        selectedSpreadsheet ? "Cargando unidades" : "Buscando inventario"
      );
      await fetchInventory();
    };

    performFetch();
  }, [selectedProject, selectedSpreadsheet, unitsList]);

  const fetchInventory = async () => {
    const spreadSheetName = selectedSpreadsheet?.value || undefined;

    const response = await fetch_inventory(
      clientId,
      selectedProject?.value,
      spreadSheetName
    );
    if (showLogs.current) console.log("fetchInventory", response);

    if (
      !response.spreadsheets &&
      (response?.error ||
        response?.message ||
        response.response?.ERROR ||
        response.response?.message)
    ) {
      const messages = processErrorMessages(response);
      showErrorFeedback({
        title: response.message || response.response?.message,
        messages,
      });
      return;
    }

    if (response.spreadsheets) {
      //Multi spreadsheet case
      const spreadsheetOptions = response.spreadsheets.map((sheet) => {
        return { value: sheet, label: sheet };
      });

      setSpreadsheetList(
        spreadsheetOptions.sort((a, b) => a.label.localeCompare(b.label))
      );
      setIsLoading(undefined);
    } else {
      processUnitsList(response);
    }
  };

  const processUnitsList = (response) => {
    unitsBaseUrl.current = response?.urlInventario;

    contenidoRef.current = response?.urlInventario?.includes("docs.google.com"); //Define si se está usando el evolutivo7.

    hasAreasExactas.current =
      response?.units?.[0]["DIMENSIONES APROX"] &&
      response?.units?.[0]["DIMENSIONES EXACTAS"];

    const unitsFiltered = response?.units?.filter(
      (row) =>
        (row["IDPRODUCTO"] || row["CODIGO INMUEBLE"]) && row["AGRUPACION"]
    );

    if (unitsFiltered?.length === 0 || !unitsFiltered) {
      showErrorFeedback({
        title: (
          <span>
            No se encontraron unidades configuradas correctamente para Plano
            Tipo.
            <br />
            Revisar columnas "IDPRODUCTO | CODIGO INMUEBLE" y "AGRUPACION" en el
            inventario.
          </span>
        ),
        messages: [],
      });
      return;
    }

    const unitsOptions = unitsFiltered?.map((row) => {
      return {
        value: row["IDPRODUCTO"] || row["CODIGO INMUEBLE"],
        label:
          row[process.env.REACT_APP_SELECTOR_UNITS_LABEL] || row["AGRUPACION"],
      };
    });

    // unitsOptions.sort((a, b) => a.label.localeCompare(b.label));

    setUnitsList(unitsOptions);
    setIsLoading(undefined);
  };
  //#endregion PROCESS INVENTORY -----------------------------------------------------

  //#region SELECTED UNIT --------------------------------------------------------
  useEffect(() => {
    setPdfDoc(undefined);
    setErrorData(undefined);
    if (!selectedUnit && abortRef.current && unitsList) {
      abortRef.current.abort();
      setIsLoading(false);
    }
  }, [selectedUnit]);

  const getPdf = async () => {
    abortRef.current = new AbortController();
    setPdfDoc(undefined);
    setErrorData(undefined);

    if (!selectedUnit) {
      abortRef.current.abort();
      return;
    }

    const fetchPdf = async () => {
      let success = false;

      try {
        const spreadsheetName = selectedSpreadsheet?.value;

        const body = {
          proyecto: selectedProject.value,
          unidad: selectedUnit.value,
          spreadsheetName,
          contenido: contenidoRef.current,
        };
        // console.log("fetchPdf", body);
        const response = await fetch_pdf(
          clientId,
          body,
          abortRef.current.signal
        );

        if (response?.error || response?.message) {
          const messages = processErrorMessages(response);
          showErrorFeedback({ title: response.message, messages });
          return;
        }

        if (!response) {
          //Signal aborted
          abortRef.current = undefined;
          return;
        }

        if (showLogs.current) console.log("pdf response", response);
        setPdfDoc(response);
        setIsLoading("Procesando documento"); // 2. Update status instead of finishing
        success = true; // 3. Mark as success
      } catch (error) {
        if (error.name !== "AbortError") {
          // Handle other errors if needed
          console.error("Fetch error:", error);
        }
      } finally {
        if (!success) {
          setIsLoading(false);
        }
      }
    };

    setIsLoading("Descargando PDF");
    fetchPdf();

    return () => {
      abortRef.current.abort();
    };
  };
  //#endregion SELECTED UNIT -----------------------------------------------------

  //#region Show error feedback --------------------------------------------------------
  const showErrorFeedback = (
    errorData,
    clearUnits = false,
    clearProjects = false
  ) => {
    if (showLogs.current) console.log("showErrorFeedback", errorData);
    if (clearUnits) setUnitsList(undefined);
    if (clearProjects) setProjectList(undefined);
    setErrorData(errorData);
    setIsLoading(undefined);
  };

  const processErrorMessages = (response) => {
    return Object.keys(response).map((key, index) => (
      <div key={index}>
        <b>{key}: </b>
        {typeof response[key] === "string" && response[key].includes("http") ? (
          <a href={response[key]} target="_blank" rel="noreferrer">
            {response[key]}
          </a>
        ) : (
          <pre style={{ whiteSpace: "pre-wrap" }}>
            {typeof response[key] === "object"
              ? JSON.stringify(response[key], null, 2)
              : String(response[key])}
          </pre>
        )}
      </div>
    ));
  };
  //#endregion Show error feedback -----------------------------------------------------

  return (
    <div
      className={`plano-tipo-container ${pdfDoc || errorData ? "scroll" : ""}`}
    >
      {projectList && (
        <Selector
          callback={setSelectedProject}
          label={
            <>
              {!dataBaseUrl.current ? (
                "Proyectos:"
              ) : (
                <a
                  className="link-icon-a"
                  href={dataBaseUrl.current}
                  target="_blank"
                  rel="noreferrer"
                  title="Ver listado de proyectos publicados para Plano Tipo"
                >
                  Proyectos:
                  <img
                    className="link-icon"
                    alt="external-link.png"
                    src="https://archibot-hub.s3.amazonaws.com/_ui-Assets/external-link.png"
                  />
                </a>
              )}
            </>
          }
          placeholder={"Selecciona un proyecto..."}
          options={projectList}
        />
      )}
      {spreadsheetList && (
        <Selector
          callback={setSelectedSpreadsheet}
          label="Inventarios:"
          placeholder={"Selecciona un inventario..."}
          options={spreadsheetList}
        />
      )}
      {unitsList && (
        <>
          <Selector
            callback={setSelectedUnit}
            label={
              <a
                className="link-icon-a"
                href={unitsBaseUrl.current}
                target="_blank"
                rel="noreferrer"
                title="Ver listado de unidades"
              >
                Unidades:
                <img
                  className="link-icon"
                  alt="external-link.png"
                  src="https://archibot-hub.s3.amazonaws.com/_ui-Assets/external-link.png"
                />
              </a>
            }
            placeholder={"Selecciona una unidad..."}
            options={unitsList}
          />
          <TogglesContenido
            contenidoRef={contenidoRef}
            hasAreasExactas={hasAreasExactas.current}
          />
          {selectedUnit && !isLoading && !pdfDoc && (
            <button onClick={getPdf} className="getpdf-button ">
              {/* login__button */}
              Solicitar PDF
            </button>
          )}
        </>
      )}
      {/* {<PdfViewer />} */}
      {(pdfDoc?.url || pdfDoc?.docs) && (
        <PdfViewer
          pdfResponse={pdfDoc}
          onLoadComplete={() => setIsLoading(false)}
        />
      )}
      {isLoading && <Loader myText={isLoading} />}
      {errorData && <WarningFormat errorData={errorData} />}
    </div>
  );
}
